import { useEffect, useRef, useState } from 'react'
import { useTheme, useTextTheme } from '../../theme/hooks'

type CodeInputProps = {
  length?: number
  onComplete: (code: string) => void
  loading?: boolean
  error?: string
  // border, ring and loader colour, defaults to the theme focus colour
  accentColor?: string
}

const CodeInput = ({
  length = 6,
  onComplete,
  loading = false,
  error,
  accentColor,
}: CodeInputProps) => {
  const { theme } = useTheme()
  const accent = accentColor || theme.border.focus
  const textTheme = useTextTheme()
  const [code, setCode] = useState<string[]>(new Array(length).fill(''))
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, value: string) => {
    // only allow single digit
    if (value.length > 1) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // move to next input if current is filled
    if (value && index < length - 1) {
      setActiveIndex(index + 1)
      inputRefs.current[index + 1]?.focus()
    }

    // check if all digits are filled
    if (
      newCode.every(digit => digit !== '') &&
      newCode.join('').length === length
    ) {
      onComplete(newCode.join(''))
    }
  }

  // handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')

    if (pastedData.length === length) {
      const pastedCode = pastedData.split('')
      setCode(pastedCode)
      setActiveIndex(length - 1)
      inputRefs.current[length - 1]?.focus()
      onComplete(pastedData)
    } else if (pastedData.length > 0) {
      // fill available slots with pasted data
      const newCode = [...code]
      for (let i = 0; i < Math.min(pastedData.length, length); i++) {
        newCode[i] = pastedData[i]
      }
      setCode(newCode)
      const nextIndex = Math.min(pastedData.length, length - 1)
      setActiveIndex(nextIndex)
      inputRefs.current[nextIndex]?.focus()
    }
  }

  // handle key down
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // move to previous input if current is empty
        setActiveIndex(index - 1)
        inputRefs.current[index - 1]?.focus()
      } else {
        // clear current input
        const newCode = [...code]
        newCode[index] = ''
        setCode(newCode)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveIndex(index - 1)
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      setActiveIndex(index + 1)
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleFocus = (index: number) => {
    setActiveIndex(index)
  }

  useEffect(() => {
    if (error) {
      setCode(new Array(length).fill(''))
      setActiveIndex(0)
      inputRefs.current[0]?.focus()
    }
  }, [error, length])

  const getInputStyle = (index: number, digit: string) => {
    if (error) {
      return {
        borderColor: theme.status.error.border,
        backgroundColor: theme.status.error.bg,
        color: textTheme.primary,
      }
    }
    if (activeIndex === index) {
      return {
        borderColor: accent,
        backgroundColor: theme.status.info.bg,
        color: textTheme.primary,
      }
    }
    if (digit) {
      return {
        borderColor: accentColor || theme.status.success.border,
        backgroundColor: accentColor ? theme.input.bg : theme.status.success.bg,
        color: textTheme.primary,
      }
    }
    return {
      borderColor: accentColor || theme.border.primary,
      backgroundColor: theme.input.bg,
      color: textTheme.primary,
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-center space-x-3">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={el => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            onPaste={handlePaste}
            disabled={loading}
            className={`h-14 w-14 rounded-lg border-2 text-center text-2xl font-bold transition-all duration-200 ${
              loading ? 'cursor-not-allowed opacity-50' : 'focus:outline-none focus:ring-2 focus:ring-opacity-50'
            }`}
            style={{
              ...getInputStyle(index, digit),
              ...(loading ? {} : { '--tw-ring-color': accent } as React.CSSProperties),
            }}
          />
        ))}
      </div>

      {error && (
        <p className="text-center text-sm" style={{ color: theme.status.error.text }}>
          {error}
        </p>
      )}

      {loading && (
        <div className="flex justify-center">
          <div className="flex items-center space-x-2" style={{ color: accent }}>
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm">Vérification en cours...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CodeInput
